import { AdminPageHeader } from '@/components/patterns/admin-page'
import ProductForm from '@/components/forms/ProductForm'
import { useAbility } from '@/lib/casl'
import {
  findSubscriptionPackageCollection,
  subscriptionPackageCollectionFilters,
} from '@/lib/subscription-package-collection'
import { collectionQueries } from '@/lib/services/collections/collections.query'
import {
  locationFilters,
  locationQueries,
  useLocations,
} from '@/lib/services/locations/locations.query'
import {
  priceAdjustmentFilters,
  priceAdjustmentQueries,
  supportsPriceAdjustmentsForOrganizationId,
  usePriceAdjustments,
} from '@/lib/services/price-adjustments/priceAdjustments.query'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { Flower2 } from 'lucide-react'

const locationQueryFilters = () => locationFilters().get()

export const Route = createFileRoute('/_authenticated/$orgId/products/create')({
  beforeLoad: ({ context }) => {
    if (!context.ability.can('create', 'products')) throw notFound()
  },
  loader: async ({ context, params }) => {
    void context.queryClient.ensureQueryData(
      locationQueries.index({
        organizationId: params.orgId,
        filters: locationQueryFilters(),
      })
    )

    if (
      context.ability?.can?.('edit', 'products') &&
      supportsPriceAdjustmentsForOrganizationId(params.orgId)
    ) {
      void context.queryClient.ensureQueryData(
        priceAdjustmentQueries.index({
          organizationId: params.orgId,
          filters: priceAdjustmentFilters,
        })
      )
    }

    const subscriptionCollection = context.ability.can('view', 'collections')
      ? findSubscriptionPackageCollection(
          await context.queryClient.ensureQueryData(
            collectionQueries.index({
              organizationId: params.orgId,
              filters: subscriptionPackageCollectionFilters,
            })
          )
        )
      : null

    return { isReadOnly: false, subscriptionCollection }
  },
  component: ProductCreatePage,
})

export function ProductCreatePage() {
  const { orgId } = Route.useParams()
  const { subscriptionCollection } = Route.useLoaderData()
  const ability = useAbility()
  const { data: locations } = useLocations({
    organizationId: orgId,
    filters: locationQueryFilters(),
  })
  const formProps = {
    locations,
    organizationId: orgId,
    subscriptionCollection,
  }
  const canLoadActivePriceAdjustments =
    ability.can('edit', 'products') && supportsPriceAdjustmentsForOrganizationId(orgId)

  return (
    <div className="space-y-4">
      <AdminPageHeader icon={Flower2} title="Create Product" />
      {canLoadActivePriceAdjustments ? (
        <ProductCreateFormWithPriceAdjustments {...formProps} />
      ) : (
        <ProductForm {...formProps} />
      )}
    </div>
  )
}

function ProductCreateFormWithPriceAdjustments({ organizationId, ...props }) {
  const { data: priceAdjustments = [] } = usePriceAdjustments({ organizationId })

  return (
    <ProductForm
      {...props}
      organizationId={organizationId}
      activePriceAdjustments={priceAdjustments}
    />
  )
}
